import { Button, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState, type Key } from 'react';
import CardTable from '../../components/Table/CardTable';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { useClients } from '../../context/ClientContext';
import { addUsersToTenant, getAvailableUsersByTenant } from '../../infrastructure/apiClient';
import type { UserDetails } from '../../infrastructure/models';
import { useTableState } from '../../hooks/useTableState';
import { sortData, sortConfig, type SortConfig } from '../../utils/sortUtils';
import styles from './AssignUser.module.scss';

interface AssignUserProps {
  onBack: () => void;
  onAssigned: () => void;
}

const SORT_CONFIGS: SortConfig<UserDetails>[] = [
  sortConfig('name', 'string'),
  sortConfig('email', 'string'),
  sortConfig('isAdmin', 'boolean'),
  sortConfig('isActive', 'boolean'),
];

const AssignUser = ({ onBack, onAssigned }: AssignUserProps) => {
  const { activeClient } = useClients();
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const {
    pagination,
    setPagination,
    sorter,
    tablePagination,
    handleTableChange,
    resetPagination,
  } = useTableState<UserDetails>();

  const fetchUsers = useCallback(async (tenantId: string, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAvailableUsersByTenant(tenantId, signal);
      setUsers(response ?? []);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: response?.length ?? 0,
      }));
    } catch (err) {
      if (!signal?.aborted) {
        setUsers([]);
        resetPagination();
        setError('Unable to load users for this tenant.');
        message.error('Failed to load users');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [setPagination, resetPagination]);

  useEffect(() => {
    if (!activeClient?.id) {
      setUsers([]);
      resetPagination();
      return;
    }
    const controller = new AbortController();
    fetchUsers(activeClient.id, controller.signal);
    return () => controller.abort();
  }, [activeClient?.id, fetchUsers, resetPagination]);

  const columns: ColumnsType<UserDetails> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
    },
    {
      title: 'Email Address',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
      sortOrder: sorter.columnKey === 'email' ? sorter.order : null,
    },
    {
      title: 'Role',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      sorter: (a, b) => Number(Boolean(a.isAdmin)) - Number(Boolean(b.isAdmin)),
      sortOrder: sorter.columnKey === 'isAdmin' ? sorter.order : null,
      render: (value: UserDetails['isAdmin']) => (
        <Tag className={`!mr-0 ${value ? styles.adminTag : ''}`}>
          {value ? 'Admin' : 'General'}
        </Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: (a, b) => Number(Boolean(a.isActive)) - Number(Boolean(b.isActive)),
      sortOrder: sorter.columnKey === 'isActive' ? sorter.order : null,
      render: (value: UserDetails['isActive']) => (
        <Tag className="!mr-0" color={value === false ? 'default' : 'green'}>
          {value === false ? 'Inactive' : 'Active'}
        </Tag>
      ),
    },
  ];

  const sortedUsers = useMemo(() => sortData(users, sorter, SORT_CONFIGS), [sorter, users]);

  const pagedUsers = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    return sortedUsers.slice(start, start + pagination.pageSize);
  }, [pagination.current, pagination.pageSize, sortedUsers]);

  const handleAssign = async () => {
    if (!activeClient?.id) {
      message.error('No active tenant selected');
      return;
    }

    try {
      const userIds = selectedRowKeys.map((key) => String(key));
      await addUsersToTenant(activeClient.id, userIds);
      message.success(`Assigned ${selectedRowKeys.length} user(s) to ${activeClient?.name ?? 'tenant'}`);
      setSelectedRowKeys([]);
      onAssigned();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to assign users');
    }
  };

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
  };

  const handleConfirmAssign = async () => {
    setConfirmLoading(true);
    try {
      await handleAssign();
    } finally {
      setConfirmLoading(false);
      handleCloseConfirm();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Assign user to {activeClient?.name ?? 'tenant'}</h1>
        <div className={styles.actions}>
          <Button onClick={onBack} type="default">
            Back
          </Button>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <CardTable<UserDetails>
          className={styles.cardTable}
          title="Users"
          columns={columns}
          dataSource={pagedUsers}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={tablePagination}
          onChange={handleTableChange}
          error={error}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
        />
      </div>
      <div className={styles.footer}>
        <Button type="primary" onClick={handleOpenConfirm} disabled={selectedRowKeys.length === 0}>
          Assign selected user(s)
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Assign selected users"
        message={`Assign ${selectedRowKeys.length} user(s) to ${activeClient?.name ?? 'this tenant'}?`}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmAssign}
        onCancel={handleCloseConfirm}
      />
    </div>
  );
};

export default AssignUser;
